import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BreadcrumbMeta,
  CanonicalPageResolver,
  CanonicalUrlOptions,
  CmsService,
  Page,
  PageBreadcrumbResolver,
  PageDescriptionResolver,
  PageLinkService,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  RoutingPageMetaResolver,
} from '@spartacus/core';
import { Observable, combineLatest, defer, of } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ResolversTextVariablesService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class CustomBasePageMetaResolver
  implements
    PageTitleResolver,
    PageDescriptionResolver,
    PageBreadcrumbResolver,
    PageRobotsResolver,
    CanonicalPageResolver
{
  private page$: Observable<Page> = defer(() => this.cmsService.getCurrentPage()).pipe(filter(Boolean));

  private title$: Observable<string | undefined> = this.page$.pipe(map((p) => p.title));

  private description$: Observable<string | undefined> = this.page$.pipe(map((p) => p.description));

  private robots$: Observable<PageRobotsMeta[]> = this.page$.pipe(map((page) => page.robots || []));

  private homeBreadcrumb$: Observable<BreadcrumbMeta[]> = of([{ label: 'главная', link: '/' }] as BreadcrumbMeta[]);

  private breadcrumb$: Observable<BreadcrumbMeta[]> = combineLatest([
    this.homeBreadcrumb$,
    defer(() => this.routingPageMetaResolver?.resolveBreadcrumbs()),
  ]).pipe(
    map((breadcrumbs) => breadcrumbs.flat()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private cmsService: CmsService,
    private routingPageMetaResolver: RoutingPageMetaResolver,
    private router: Router,
    private pageLinkService: PageLinkService,
    private resolversTextVariablesService: ResolversTextVariablesService,
  ) {}

  resolveTitle(): Observable<string> {
    return this.title$.pipe(
      switchMap((title) => {
        if (title) {
          return this.resolversTextVariablesService.resolveTemplateString(title);
        }

        return of('');
      }),
    );
  }

  resolveDescription(): Observable<string | undefined> {
    return this.description$.pipe(
      switchMap((description) => {
        if (description) {
          return this.resolversTextVariablesService.resolveTemplateString(description);
        }

        return of('');
      }),
    );
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest({ breadcrumbs: this.breadcrumb$, title: this.resolveTitle() }).pipe(
      map(({ breadcrumbs, title }) => [...breadcrumbs.flat(), { label: title } as BreadcrumbMeta]),
    );
  }

  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.robots$;
  }

  resolveCanonicalUrl(options?: CanonicalUrlOptions): Observable<string> {
    return this.router.events.pipe(
      filter((ev) => ev instanceof NavigationEnd),
      startWith(null),
      map(() => this.pageLinkService.getCanonicalUrl(options)),
    );
  }

  resolveType(): Observable<string> {
    return of('website');
  }
}
